import { TranslationKey } from '@/contexts/translations';
import { NavButton } from './nav-button';
import { SaveProgressButton, SaveProgressButtonProps } from './save-progress-button';
import styles from './visit-title-buttons.module.css';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useVisitNavigation } from '@/contexts/visit-navigation-context';
import clsx from 'clsx';
import { createEditUrl } from '@/util/helpers';

interface ButtonConfig {
  to: string;
  text?: TranslationKey;
}

interface TitleButtonsProps {
  backButton?: ButtonConfig;
  saveProgress?: SaveProgressButtonProps;
  showNextButtonOnReview?: boolean;
  showBackButtonOnReview?: boolean;
  className?: string;
  logo?: React.ReactNode;
  hideSaveProgress?: boolean;
}

export function VisitTitleButtons({
  backButton,
  saveProgress,
  showNextButtonOnReview,
  showBackButtonOnReview,
  className,
  logo,
  hideSaveProgress = false,
}: TitleButtonsProps) {
  const isDesktop = useMediaQuery('--screen-lg');
  const { hasCompletedStep, nextStep, currentStepStatusId, prevStep, editMode } =
    useVisitNavigation();

  const backButtonPath = backButton?.to ?? prevStep?.path;
  const backButtonText = backButton?.text;

  const showNextButton =
    currentStepStatusId &&
    hasCompletedStep(currentStepStatusId) &&
    nextStep &&
    !nextStep.nonNavigable;

  const showBackButton = backButtonPath && (!!backButton?.to || !prevStep?.nonNavigable);

  return (
    <div className={clsx(styles.titleButtons, className)}>
      {!editMode ? (
        <div className={clsx(styles.navButtons, logo && styles.fixedWidth)}>
          {showBackButton && (
            <NavButton
              to={backButtonPath}
              text={backButtonText}
              className={styles.visitNavigation}
            />
          )}
          {showNextButton && (
            <>
              {showBackButton && <div className={styles.divider} />}
              <NavButton
                to={nextStep.path}
                text="common.nextStep"
                type="next"
                className={styles.visitNavigation}
              />
            </>
          )}
        </div>
      ) : (
        <>
          {showBackButtonOnReview && showBackButton && (
            <NavButton
              to={createEditUrl(backButtonPath, editMode)}
              text={backButtonText}
              className={styles.visitNavigation}
            />
          )}
          {showNextButtonOnReview && showNextButton && (
            <>
              {showBackButtonOnReview && showBackButton && <div className={styles.divider} />}
              <NavButton
                to={createEditUrl(nextStep.path, editMode)}
                text={'common.nextStep'}
                type="next"
                className={styles.visitNavigation}
              />
            </>
          )}
        </>
      )}
      {isDesktop && logo}
      {isDesktop && !hideSaveProgress && (
        <SaveProgressButton
          className={clsx(logo ? styles.fixedWidth : styles.saveProgressButton)}
          {...saveProgress}
        />
      )}
    </div>
  );
}
